import { initContract } from '@ts-rest/core'
import { z } from 'zod'
import {
    GetCategoriesResponseSchema,
    GetCategoryStatsQuerySchema,
    GetConsistentLabelSendersQuerySchema,
    GetConsistentLabelSendersResponseSchema,
    GetEmailsQuerySchema,
    GetEmailsResponseSchema,
    GetLabelAnalysisQuerySchema,
    GetLabelAnalysisResponseSchema,
    GetSendersByCategoryQuerySchema,
    GetSendersByCategoryResponseSchema,
    GetSendersQuerySchema,
    GetSendersResponseSchema,
} from '../schemas/manage'
import { defaultErrorResponses } from './errors'

const c = initContract()

/**
 * Inbox Management Contract - Analytics, sender aggregation, email browsing, and labeling insights.
 * All endpoints are read-only. Aggregations computed from emails table with optional Gmail sync.
 * All endpoints require authMiddleware.
 */
export const manageContract = c.router({
  /**
   * Lists all senders with email counts, total sizes, and recent activity.
   * Gets: limit (default: 20, max: 100), offset from query.
   * Returns: GetSendersResponseSchema with senders: { email, count, total_bytes, last_received_at }.
   * Side Effects: None (read-only).
   */
  listSenders: {
    method: 'GET',
    path: '/v1/manage/senders',
    query: GetSendersQuerySchema,
    responses: {
      200: GetSendersResponseSchema,
      ...defaultErrorResponses,
    },
    metadata: {
      auth: true,
      summary: 'List senders with counts and sizes',
      description: 'Lists all senders with email counts and total bytes for inbox overview.',
    },
  },
  /**
   * Aggregates email stats by category (Attention/Archive/Trash). Shows counts and sizes per category.
   * Gets: limit (default: 20, max: 100), offset from query.
   * Returns: GetCategoriesResponseSchema with categories: { track, count, total_bytes }.
   * Side Effects: None (read-only).
   */
  listCategories: {
    method: 'GET',
    path: '/v1/manage/categories',
    query: GetCategoryStatsQuerySchema,
    responses: {
      200: GetCategoriesResponseSchema,
      ...defaultErrorResponses,
    },
    metadata: {
      auth: true,
      summary: 'Aggregate counts/sizes by category',
      description: 'Email statistics aggregated by classification track (Attention/Archive/Trash).',
    },
  },
  /**
   * Retrieves all senders within a specific category with their email counts.
   * Gets: category ('Attention'|'Archive'|'Trash') path param, limit/offset from query.
   * Returns: GetSendersByCategoryResponseSchema with senders for that category.
   * Side Effects: None (read-only).
   */
  listSendersByCategory: {
    method: 'GET',
    path: '/v1/manage/categories/:category/senders',
    query: GetSendersByCategoryQuerySchema,
    pathParams: z.object({ category: z.string() }),
    responses: {
      200: GetSendersByCategoryResponseSchema,
      ...defaultErrorResponses,
    },
    metadata: {
      auth: true,
      summary: 'Senders for a category',
      description: 'Lists senders with emails in a specific classification category.',
    },
  },
  /**
   * Fetches email previews filtered by sender, category, or search term with pagination.
   * Gets: sender (email address), category, search term, limit, offset from query.
   * Returns: GetEmailsResponseSchema with email_previews: { id, subject, from, snippet, date }.
   * Side Effects: None (read-only).
   */
  listEmails: {
    method: 'GET',
    path: '/v1/manage/emails',
    query: GetEmailsQuerySchema,
    responses: {
      200: GetEmailsResponseSchema,
      ...defaultErrorResponses,
    },
    metadata: {
      auth: true,
      summary: 'Email previews by sender or category',
      description: 'Browse email previews filtered by sender, category, or search query.',
    },
  },
  /**
   * Identifies senders with emails inconsistently assigned to multiple labels (possible labeling confusion).
   * Gets: limit/offset from query.
   * Returns: GetLabelAnalysisResponseSchema with senders having mixed label assignments.
   * Side Effects: None (read-only).
   */
  labelAnalysis: {
    method: 'GET',
    path: '/v1/manage/label-analysis',
    query: GetLabelAnalysisQuerySchema,
    responses: {
      200: GetLabelAnalysisResponseSchema,
      ...defaultErrorResponses,
    },
    metadata: {
      auth: true,
      summary: 'Senders with mixed labels',
      description: 'Identifies senders with inconsistent label assignments across emails.',
    },
  },
  /**
   * Retrieves senders whose emails are consistently assigned to the same label across inbox.
   * Gets: label (name) from query.
   * Returns: GetConsistentLabelSendersResponseSchema with senders having consistent label.
   * Side Effects: None (read-only).
   */
  consistentLabelSenders: {
    method: 'GET',
    path: '/v1/manage/consistent-label-senders',
    query: GetConsistentLabelSendersQuerySchema,
    responses: {
      200: GetConsistentLabelSendersResponseSchema,
      ...defaultErrorResponses,
    },
    metadata: {
      auth: true,
      summary: 'Senders with consistent labels',
      description: 'Lists senders whose emails are consistently labeled.',
    },
  },
})
